import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function SignupSearch({ api }) {
    const [confirmationId, setConfirmationId] = useState('');
    const [searchPerformed, setSearchPerformed] = useState(false);
    const queryClient = useQueryClient();

    const { data: signup, isLoading, error, refetch } = useQuery({
        queryKey: ['signup-search', confirmationId],
        queryFn: () => api.searchSignupByConfirmationId(confirmationId),
        enabled: false, // Don't auto-fetch, only when search is performed
    });

    // Mutation for updating payment status
    const updatePaymentMutation = useMutation({
        mutationFn: ({ confirmationId, isPaid }) => api.updateSignupPaymentStatus(confirmationId, isPaid),
        onSuccess: () => {
            // Refetch the signup data to show updated status
            refetch();
        },
    });

    const handleSearch = (e) => {
        e.preventDefault();
        if (confirmationId.trim()) {
            setSearchPerformed(true);
            refetch();
        }
    };

    const handleClear = () => {
        setConfirmationId('');
        setSearchPerformed(false);
    };

    return (
        <div className="rounded-lg border bg-card p-6 shadow-sm">
            <div className="flex items-center space-x-2 mb-4">
                <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <span className="text-blue-600 text-sm">🔍</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground">Search Signups</h3>
            </div>

            <form onSubmit={handleSearch} className="space-y-4">
                <div className="space-y-2">
                    <label htmlFor="confirmation-id" className="text-sm font-medium text-foreground">
                        Confirmation Number
                    </label>
                    <div className="flex space-x-2">
                        <input
                            id="confirmation-id"
                            type="text"
                            value={confirmationId}
                            onChange={(e) => setConfirmationId(e.target.value.toUpperCase())}
                            placeholder="e.g., A7B2X9"
                            className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 uppercase"
                            maxLength={6}
                        />
                        <button
                            type="submit"
                            disabled={!confirmationId.trim() || isLoading}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Searching...
                                </>
                            ) : (
                                'Search'
                            )}
                        </button>
                    </div>
                </div>
            </form>

            {/* Search Results */}
            {searchPerformed && (
                <div className="mt-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            <span className="ml-2">Searching...</span>
                        </div>
                    ) : error ? (
                        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-destructive">⚠️</span>
                                <p className="text-sm text-destructive">
                                    {error.message === 'Failed to fetch: 404'
                                        ? 'No signup found with that confirmation number.'
                                        : `Search failed: ${error.message}`}
                                </p>
                            </div>
                        </div>
                    ) : signup ? (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-foreground">Signup Found</h4>
                                <button
                                    onClick={handleClear}
                                    className="text-sm text-muted-foreground hover:text-foreground"
                                >
                                    Clear
                                </button>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                {/* Confirmation Details */}
                                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                                    <span className="text-sm font-medium text-gray-700">Confirmation #:</span>
                                    <span className="text-lg font-bold text-gray-900 font-mono">{signup.signupId}</span>
                                </div>

                                {/* Payment Status */}
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-sm font-medium text-gray-700">Payment Status:</span>
                                    <div className="flex items-center space-x-2">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${signup.isPaid
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                            }`}>
                                            {signup.isPaid ? 'Paid' : 'Not Paid'}
                                        </span>
                                        <button
                                            onClick={() => updatePaymentMutation.mutate({
                                                confirmationId: signup.signupId,
                                                isPaid: !signup.isPaid
                                            })}
                                            disabled={updatePaymentMutation.isPending}
                                            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {updatePaymentMutation.isPending ? 'Updating...' : 'Toggle'}
                                        </button>
                                    </div>
                                </div>

                                {/* Participant Details */}
                                <div className="space-y-2">
                                    <h5 className="font-medium text-gray-900">Participant Information</h5>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-gray-600">Name:</span>
                                            <span className="ml-2 font-medium">{signup.participantName}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Home Town:</span>
                                            <span className="ml-2 font-medium">{signup.homeTown}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Horses */}
                                <div className="space-y-2">
                                    <h5 className="font-medium text-gray-900">Horses</h5>
                                    <div className="flex flex-wrap gap-2">
                                        {signup.horses.map((horse, index) => (
                                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                                {horse}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Class Entries */}
                                <div className="space-y-2">
                                    <h5 className="font-medium text-gray-900">Class Entries</h5>
                                    <div className="space-y-2">
                                        {signup.classEntries.map((entry, index) => (
                                            <div key={index} className="p-3 bg-white rounded border">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <p className="font-medium text-sm">Class Entry {index + 1}</p>
                                                        <p className="text-sm text-gray-600">
                                                            Horses: {entry.selectedHorses.join(', ')}
                                                        </p>
                                                    </div>
                                                    <span className="text-sm font-semibold text-green-600">
                                                        ${entry.entryCost.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Total Cost */}
                                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                    <span className="text-lg font-semibold text-gray-900">Total Cost:</span>
                                    <span className="text-xl font-bold text-green-600">${signup.totalCost.toFixed(2)}</span>
                                </div>

                                {/* Signup Date */}
                                <div className="text-sm text-gray-500 pt-2 border-t border-gray-200">
                                    Signed up: {new Date(signup.createdAt).toLocaleDateString()} at {new Date(signup.createdAt).toLocaleTimeString()}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
} 